import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Loader2, Plus, Trash2, GripVertical, Info } from "lucide-react";

/**
 * Iter55d — Trusted Worldwide editor.
 * Manages the CMS-driven credibility strip below the hero. Fully additive —
 * admin can rename the section, add/remove countries, reorder or disable
 * individually without touching code.
 */

let _seq = 0;
const nextId = (name) => `${(name || "country").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 32)}-${(_seq++).toString(36)}`;

const TrustedWorldwideEditor = () => {
  const [section, setSection] = useState(null);
  const [countries, setCountries] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/admin/settings/all");
      const t = data.homepage_trusted || {};
      setSection(t.section || { title: "Trusted worldwide", subtitle: "" });
      setCountries((t.countries || []).map((c) => ({ ...c, id: c.id || nextId(c.name) })));
    } catch (e) {
      toast.error("Failed to load Trusted section", { description: e.response?.data?.detail || "" });
    }
  })(); }, []);

  const save = async () => {
    if (!section || !countries) return;
    setSaving(true);
    try {
      const ordered = countries.map((c, i) => ({ ...c, order: i }));
      await api.put("/admin/settings/homepage_trusted", { data: { section, countries: ordered } });
      toast.success("Trusted section saved");
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const addCountry = () => setCountries((c) => [...c, { id: nextId("new"), name: "", enabled: true }]);
  const removeCountry = (id) => setCountries((c) => c.filter((r) => r.id !== id));
  const patch = (id, p) => setCountries((c) => c.map((r) => (r.id === id ? { ...r, ...p } : r)));

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx == null || dragIdx === i) return;
    setCountries((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIdx(null);
  };

  if (!section || !countries) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="trusted-editor">
      <div className="border border-border bg-accent/5 p-4 flex items-start gap-3">
        <Info size={16} className="text-accent shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">
          Slim credibility strip rendered directly below the Hero on the public homepage.
          Add or remove markets as your business expands. Drag rows to change the display order.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="overline text-muted-foreground text-xs">Section title</Label>
          <Input value={section.title || ""} onChange={(e) => setSection({ ...section, title: e.target.value })} placeholder="Trusted worldwide" className="rounded-none h-10 mt-1" data-testid="trusted-title" />
        </div>
        <div>
          <Label className="overline text-muted-foreground text-xs">Subtitle (optional)</Label>
          <Input value={section.subtitle || ""} onChange={(e) => setSection({ ...section, subtitle: e.target.value })} placeholder="…and 30+ more countries" className="rounded-none h-10 mt-1" data-testid="trusted-subtitle" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="overline text-muted-foreground text-xs">Countries · {countries.length}</div>
        <Button variant="outline" onClick={addCountry} className="rounded-none h-9" data-testid="trusted-add"><Plus size={14} className="mr-2" />Add country</Button>
      </div>

      <div className="space-y-2">
        {countries.map((c, idx) => (
          <div
            key={c.id}
            draggable
            onDragStart={() => onDragStart(idx)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(idx)}
            data-testid={`trusted-row-${idx}`}
            className={`flex items-center gap-3 border border-border bg-card p-3 ${dragIdx === idx ? "opacity-40" : ""}`}
          >
            <span className="text-muted-foreground cursor-grab shrink-0"><GripVertical size={16} /></span>
            <span className="text-xs text-muted-foreground tabular-nums w-6">{String(idx + 1).padStart(2, "0")}</span>
            <Input value={c.name || ""} onChange={(e) => patch(c.id, { name: e.target.value })} placeholder="e.g. Germany" className="rounded-none h-9 flex-1" data-testid={`trusted-name-${idx}`} />
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={c.enabled !== false} onCheckedChange={(v) => patch(c.id, { enabled: v })} data-testid={`trusted-enabled-${idx}`} />
              <span>Enabled</span>
            </label>
            <button onClick={() => removeCountry(c.id)} className="p-1.5 text-muted-foreground hover:text-destructive" aria-label="Remove country" data-testid={`trusted-delete-${idx}`}><Trash2 size={14} /></button>
          </div>
        ))}
        {countries.length === 0 && (
          <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No countries — click "Add country" to bring the section to life.</div>
        )}
      </div>

      <div className="pt-2">
        <Button onClick={save} disabled={saving} className="rounded-none bg-primary h-11 px-8" data-testid="trusted-save">
          {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Trusted Worldwide</>}
        </Button>
      </div>
    </div>
  );
};

export default TrustedWorldwideEditor;
